/**
 * A mutable position, derived from a spec, for the search's inner loop only.
 *
 * Why this exists: running the interpreter inside the search costs a frozen
 * 42-element array copy and a 42-character key string at every node, which
 * capped a full-game review at roughly 120k nodes per second — a depth-5
 * opening, which is not an honest basis for calling somebody's move a blunder.
 * This file plays and unplays moves in place, hashes with Zobrist keys, and
 * keeps the evaluation up to date incrementally.
 *
 * That is a second implementation of the same rules, which is exactly the thing
 * this codebase refuses to do by hand — so it is *derived*, not written: the
 * landing rule comes from `moves[0].fill`, the win test from `terminal.win`, the
 * draw test from `terminal.draw`, the evaluation from the shared window
 * enumeration. `position.conformance.test.ts` then proves it agrees with the
 * interpreter, move for move, over random playouts on several specs. The
 * interpreter stays the source of truth; this is a cache with a proof.
 */
import { boardSize, colOf, indexOf, rowOf } from '../spec/interpreter.ts';
import type { BoardOf, SeatOf, State } from '../spec/interpreter.ts';
import type { RulesSpec } from '../spec/schema.ts';
import {
  WINDOW_SCALE,
  centralityTable,
  clampHeuristic,
  enumerateWinWindows,
  longestWinLength,
  windowWeight,
} from './eval.ts';

/** No occupant yet. */
const WINDOW_EMPTY = -1;
/** Two seats inside one window: it can never be completed, by anyone. */
const WINDOW_DEAD = -2;

export interface MutablePosition {
  /** The seat to move, as the spec numbers seats. */
  readonly toMove: number;
  /**
   * True when a piece can still be dropped in this column: the column has room
   * *and* the game is not already over, exactly as `legalMoves` decides it.
   */
  canPlay(param: number): boolean;
  /**
   * Play `param` for the seat to move and return the cell it filled, or -1 when
   * the column is full. Advances the turn.
   */
  play(param: number): number;
  /** Take back the last `play`. Restores turn, hash, evaluation and win flag. */
  undo(): void;
  /** True when the piece just played completed a line. */
  lastMoveWon(): boolean;
  /** True when the spec calls this position a draw. */
  isDrawn(): boolean;
  /** Zobrist key, side to move included. Safe to use as a plain `Map` key. */
  key(): number;
  /** The incremental heuristic, from `seat`'s point of view. Matches `eval.ts`. */
  evaluateFor(seat: number): number;
  /** A frozen snapshot, for tests and for crossing back to the interpreter. */
  snapshot(): readonly number[];
}

/**
 * Deterministic 32-bit noise for the Zobrist tables.
 *
 * Seeded and fixed: two runs of the same review must produce the same
 * transposition keys, or the search would not be reproducible.
 */
function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) | 0;
  };
}

/**
 * Everything derivable from a spec, computed once and shared by every position
 * of that game: window tables, cell-to-window index, Zobrist noise, centrality.
 */
export interface PositionTables {
  readonly rules: RulesSpec;
  readonly size: number;
  readonly cols: number;
  readonly rows: number;
  readonly seats: readonly number[];
  readonly seatIndex: ReadonlyMap<number, number>;
  readonly ascending: boolean;
  readonly drawable: boolean;
  readonly windowCells: Int32Array;
  readonly windowOffsets: Int32Array;
  readonly windowCount: number;
  readonly cellWindows: Int32Array;
  readonly cellWindowOffsets: Int32Array;
  readonly weights: Int32Array;
  readonly centrality: Int32Array;
  readonly zobristHi: Int32Array;
  readonly zobristLo: Int32Array;
  readonly sideHi: Int32Array;
  readonly sideLo: Int32Array;
  /** Win conditions flattened: `[length, dRow, dCol]` triples. */
  readonly lines: Int32Array;
}

const TABLE_CACHE = new WeakMap<RulesSpec, PositionTables>();

/** Build (or reuse) the derived tables for a spec. Keyed by identity, so a spec
 * object reviewed many times pays for its tables once. */
export function positionTables(rules: RulesSpec): PositionTables {
  const cached = TABLE_CACHE.get(rules);
  if (cached) return cached;

  const size = boardSize(rules);
  const windows = enumerateWinWindows(rules);
  const windowCount = windows.length;

  let total = 0;
  for (const window of windows) total += window.length;
  const windowCells = new Int32Array(total);
  const windowOffsets = new Int32Array(windowCount + 1);
  let cursor = 0;
  for (const [index, window] of windows.entries()) {
    windowOffsets[index] = cursor;
    for (const cell of window) {
      windowCells[cursor] = cell;
      cursor += 1;
    }
  }
  windowOffsets[windowCount] = cursor;

  // Inverted index: which windows pass through each cell. This is what makes
  // the evaluation incremental — a move touches a dozen windows, not all of them.
  const counts = new Int32Array(size + 1);
  for (let index = 0; index < total; index += 1) {
    const cell = windowCells[index] ?? 0;
    counts[cell] = (counts[cell] ?? 0) + 1;
  }
  const cellWindowOffsets = new Int32Array(size + 1);
  let running = 0;
  for (let cell = 0; cell < size; cell += 1) {
    cellWindowOffsets[cell] = running;
    running += counts[cell] ?? 0;
  }
  cellWindowOffsets[size] = running;
  const fill = new Int32Array(size);
  const cellWindows = new Int32Array(running);
  for (let window = 0; window < windowCount; window += 1) {
    const start = windowOffsets[window] ?? 0;
    const end = windowOffsets[window + 1] ?? start;
    for (let index = start; index < end; index += 1) {
      const cell = windowCells[index] ?? 0;
      const at = (cellWindowOffsets[cell] ?? 0) + (fill[cell] ?? 0);
      cellWindows[at] = window;
      fill[cell] = (fill[cell] ?? 0) + 1;
    }
  }

  const longest = longestWinLength(rules);
  const weights = new Int32Array(longest + 1);
  for (let count = 0; count <= longest; count += 1) weights[count] = windowWeight(count);

  const seats = [...rules.seats];
  const seatIndex = new Map(seats.map((seat, index) => [seat, index]));

  const random = mulberry32(0x9e3779b9);
  const zobristHi = new Int32Array(size * seats.length);
  const zobristLo = new Int32Array(size * seats.length);
  for (let index = 0; index < zobristHi.length; index += 1) {
    zobristHi[index] = random();
    zobristLo[index] = random();
  }
  const sideHi = new Int32Array(seats.length);
  const sideLo = new Int32Array(seats.length);
  for (let index = 0; index < seats.length; index += 1) {
    sideHi[index] = random();
    sideLo[index] = random();
  }

  const triples: number[] = [];
  for (const condition of rules.terminal.win) {
    for (const [dRow, dCol] of condition.directions) {
      if (dRow === 0 && dCol === 0) continue;
      triples.push(condition.length, dRow, dCol);
    }
  }

  const tables: PositionTables = {
    rules,
    size,
    cols: rules.board.cols,
    rows: rules.board.rows,
    seats,
    seatIndex,
    ascending: rules.moves[0].fill === 'ascending',
    drawable: rules.terminal.draw.length > 0,
    windowCells,
    windowOffsets,
    windowCount,
    cellWindows,
    cellWindowOffsets,
    weights,
    centrality: centralityTable(rules),
    zobristHi,
    zobristLo,
    sideHi,
    sideLo,
    lines: Int32Array.from(triples),
  };
  TABLE_CACHE.set(rules, tables);
  return tables;
}

/**
 * A mutable position holding the same facts as `state`.
 *
 * The board is copied, never aliased, and the caller's `State` is left frozen
 * and untouched.
 */
export function createPosition<S extends RulesSpec>(
  rules: S,
  board: BoardOf<S>,
  toMove: SeatOf<S>,
): MutablePosition {
  const tables = positionTables(rules);
  const { size, cols, rows, seats, ascending, drawable } = tables;
  const empty = rules.cells.empty;
  const seatCount = seats.length;

  const cells = new Int32Array(size);
  const seatOf = new Int32Array(size).fill(WINDOW_EMPTY);
  let filled = 0;
  for (let index = 0; index < size; index += 1) {
    const value = board[index] ?? empty;
    cells[index] = value;
    if (value !== empty) {
      seatOf[index] = tables.seatIndex.get(value) ?? WINDOW_EMPTY;
      filled += 1;
    }
  }

  // The next landing row per column, found the way the interpreter finds it, so
  // a board with holes in it behaves identically rather than merely usually.
  const landing = new Int32Array(cols).fill(-1);
  for (let col = 0; col < cols; col += 1) landing[col] = scanLanding(col, 0);

  function scanLanding(col: number, fromStep: number): number {
    for (let step = fromStep; step < rows; step += 1) {
      const row = ascending ? step : rows - 1 - step;
      const index = indexOf(rules, row, col);
      if (cells[index] === empty) return index;
    }
    return -1;
  }

  /** How far down the fill order a cell sits, so `undo` can restore the pointer. */
  function stepOf(index: number): number {
    const row = rowOf(rules, index);
    return ascending ? row : rows - 1 - row;
  }

  const occupant = new Int32Array(tables.windowCount).fill(WINDOW_EMPTY);
  const occupantCount = new Int32Array(tables.windowCount);
  const seatScore = new Float64Array(seatCount);

  function setWindow(window: number, nextOccupant: number, nextCount: number): void {
    const previous = occupant[window] ?? WINDOW_EMPTY;
    if (previous >= 0) {
      seatScore[previous] =
        (seatScore[previous] ?? 0) - (tables.weights[occupantCount[window] ?? 0] ?? 0) * WINDOW_SCALE;
    }
    occupant[window] = nextOccupant;
    occupantCount[window] = nextCount;
    if (nextOccupant >= 0) {
      seatScore[nextOccupant] =
        (seatScore[nextOccupant] ?? 0) + (tables.weights[nextCount] ?? 0) * WINDOW_SCALE;
    }
  }

  // Seed the window state and the score from the starting board.
  for (let window = 0; window < tables.windowCount; window += 1) {
    const start = tables.windowOffsets[window] ?? 0;
    const end = tables.windowOffsets[window + 1] ?? start;
    let owner = WINDOW_EMPTY;
    let count = 0;
    for (let index = start; index < end; index += 1) {
      const seat = seatOf[tables.windowCells[index] ?? 0] ?? WINDOW_EMPTY;
      if (seat === WINDOW_EMPTY) continue;
      if (count === 0) {
        owner = seat;
        count = 1;
        continue;
      }
      if (seat === owner) {
        count += 1;
        continue;
      }
      owner = WINDOW_DEAD;
      count = 0;
      break;
    }
    setWindow(window, owner, count);
  }
  for (let index = 0; index < size; index += 1) {
    const seat = seatOf[index] ?? WINDOW_EMPTY;
    if (seat < 0) continue;
    seatScore[seat] = (seatScore[seat] ?? 0) + (tables.centrality[index] ?? 0);
  }

  let hashHi = 0;
  let hashLo = 0;
  for (let index = 0; index < size; index += 1) {
    const seat = seatOf[index] ?? WINDOW_EMPTY;
    if (seat < 0) continue;
    hashHi ^= tables.zobristHi[index * seatCount + seat] ?? 0;
    hashLo ^= tables.zobristLo[index * seatCount + seat] ?? 0;
  }
  let turn = tables.seatIndex.get(toMove) ?? 0;
  hashHi ^= tables.sideHi[turn] ?? 0;
  hashLo ^= tables.sideLo[turn] ?? 0;

  let won = false;

  // Undo journal. Move records are fixed width; window records are variable, so
  // each move remembers where its slice of the window journal starts.
  const moveCells: number[] = [];
  const movePointers: number[] = [];
  const moveSeats: number[] = [];
  const moveWonFlags: boolean[] = [];
  const moveJournalStarts: number[] = [];
  const journalWindow: number[] = [];
  const journalOccupant: number[] = [];
  const journalCount: number[] = [];

  function winsThrough(index: number, seat: number): boolean {
    const row = rowOf(rules, index);
    const col = colOf(rules, index);
    for (let cursor = 0; cursor < tables.lines.length; cursor += 3) {
      const length = tables.lines[cursor] ?? 0;
      const dRow = tables.lines[cursor + 1] ?? 0;
      const dCol = tables.lines[cursor + 2] ?? 0;
      // Both ways along the direction, counting the played cell once. Unrolled:
      // this runs at every node of the search, and an iterated `[1, -1]` would
      // allocate an array for the privilege.
      let run = 1;
      let r = row + dRow;
      let c = col + dCol;
      while (run < length && r >= 0 && r < rows && c >= 0 && c < cols && seatOf[indexOf(rules, r, c)] === seat) {
        run += 1;
        r += dRow;
        c += dCol;
      }
      r = row - dRow;
      c = col - dCol;
      while (run < length && r >= 0 && r < rows && c >= 0 && c < cols && seatOf[indexOf(rules, r, c)] === seat) {
        run += 1;
        r -= dRow;
        c -= dCol;
      }
      if (run >= length) return true;
    }
    return false;
  }

  const position: MutablePosition = {
    get toMove(): number {
      return seats[turn] ?? 0;
    },

    canPlay(param: number): boolean {
      if (won || position.isDrawn()) return false;
      if (!Number.isInteger(param) || param < 0 || param >= cols) return false;
      return (landing[param] ?? -1) !== -1;
    },

    play(param: number): number {
      if (!position.canPlay(param)) return -1;
      const cell = landing[param] ?? -1;
      const seat = turn;

      cells[cell] = seats[seat] ?? 0;
      seatOf[cell] = seat;
      filled += 1;

      moveCells.push(cell);
      movePointers.push(cell);
      moveSeats.push(seat);
      moveWonFlags.push(won);
      moveJournalStarts.push(journalWindow.length);

      const start = tables.cellWindowOffsets[cell] ?? 0;
      const end = tables.cellWindowOffsets[cell + 1] ?? start;
      for (let index = start; index < end; index += 1) {
        const window = tables.cellWindows[index] ?? 0;
        const previous = occupant[window] ?? WINDOW_EMPTY;
        if (previous === WINDOW_DEAD) continue;
        journalWindow.push(window);
        journalOccupant.push(previous);
        journalCount.push(occupantCount[window] ?? 0);
        if (previous === WINDOW_EMPTY) setWindow(window, seat, 1);
        else if (previous === seat) setWindow(window, seat, (occupantCount[window] ?? 0) + 1);
        else setWindow(window, WINDOW_DEAD, 0);
      }

      seatScore[seat] = (seatScore[seat] ?? 0) + (tables.centrality[cell] ?? 0);
      hashHi ^= tables.zobristHi[cell * seatCount + seat] ?? 0;
      hashLo ^= tables.zobristLo[cell * seatCount + seat] ?? 0;

      landing[param] = scanLanding(param, stepOf(cell) + 1);
      won = winsThrough(cell, seat);

      const next = (turn + 1) % seatCount;
      hashHi ^= (tables.sideHi[turn] ?? 0) ^ (tables.sideHi[next] ?? 0);
      hashLo ^= (tables.sideLo[turn] ?? 0) ^ (tables.sideLo[next] ?? 0);
      turn = next;

      return cell;
    },

    undo(): void {
      const cell = moveCells.pop();
      const pointer = movePointers.pop();
      const seat = moveSeats.pop();
      const previousWon = moveWonFlags.pop();
      const journalStart = moveJournalStarts.pop();
      if (
        cell === undefined ||
        pointer === undefined ||
        seat === undefined ||
        previousWon === undefined ||
        journalStart === undefined
      ) {
        return;
      }

      while (journalWindow.length > journalStart) {
        const window = journalWindow.pop() ?? 0;
        setWindow(window, journalOccupant.pop() ?? WINDOW_EMPTY, journalCount.pop() ?? 0);
      }

      cells[cell] = empty;
      seatOf[cell] = WINDOW_EMPTY;
      filled -= 1;
      landing[colOf(rules, cell)] = pointer;
      seatScore[seat] = (seatScore[seat] ?? 0) - (tables.centrality[cell] ?? 0);
      hashHi ^= tables.zobristHi[cell * seatCount + seat] ?? 0;
      hashLo ^= tables.zobristLo[cell * seatCount + seat] ?? 0;
      won = previousWon;

      hashHi ^= (tables.sideHi[turn] ?? 0) ^ (tables.sideHi[seat] ?? 0);
      hashLo ^= (tables.sideLo[turn] ?? 0) ^ (tables.sideLo[seat] ?? 0);
      turn = seat;
    },

    lastMoveWon(): boolean {
      return won;
    },

    isDrawn(): boolean {
      return !won && drawable && filled === size;
    },

    key(): number {
      // 21 bits of the high word and all 32 of the low one: 53 bits, which is
      // every bit a JavaScript number can hold exactly, so `Map` keys stay cheap.
      return (hashHi & 0x1fffff) * 4294967296 + (hashLo >>> 0);
    },

    evaluateFor(seat: number): number {
      const index = tables.seatIndex.get(seat);
      if (index === undefined) return 0;
      let score = 0;
      for (let other = 0; other < seatCount; other += 1) {
        score += other === index ? (seatScore[other] ?? 0) : -(seatScore[other] ?? 0);
      }
      return clampHeuristic(score);
    },

    snapshot(): readonly number[] {
      return Object.freeze([...cells]);
    },
  };

  return position;
}

/** Convenience: a mutable position matching an interpreter `State`. */
export function positionFromState<S extends RulesSpec>(rules: S, state: State<S>): MutablePosition {
  return createPosition(rules, state.board, state.toMove);
}
